// Phase R4.0: ?ref=<affiliate_username> attribution capture.
//
// Activation flow:
//   1. User lands on serviceclub.pro/?ref=housewashpro (any page).
//   2. captureRefFromUrl() runs once at app boot (src/main.tsx),
//      stores the username + capture timestamp to localStorage.
//   3. The user signs up. AuthProvider's hydration codepath calls
//      applyAttributionIfPending(supabase, defaultSubAccountId)
//      once myMemberships populates with exactly one sub_account
//      that hasn't been attributed yet.
//   4. The RPC validates owner + active affiliate + not-self +
//      not-already-attributed and writes
//      sub_accounts.referred_by_affiliate_id +
//      attribution_timestamp.
//   5. localStorage clears on success. From then on, customers the
//      merchant creates under that sub_account inherit the
//      attribution via the BEFORE INSERT trigger
//      (populate_customer_referral_attribution).
//
// CreateSubAccountModal also calls applyAttributionIfPending after
// successful create so a Multi-Account user who clicks an affiliate
// link mid-session and creates a new business attributes that new
// business correctly.
//
// 30-day TTL: captured refs that age out are auto-purged on read.
// All RPC failures are silent (console.warn only) — attribution is
// best-effort; the app never blocks UX on it.

import type { SupabaseClient } from '@supabase/supabase-js'

const LOCALSTORAGE_KEY = 'serviceclub_referral_ref'
const MAX_AGE_DAYS = 30
const MAX_AGE_MS = MAX_AGE_DAYS * 24 * 60 * 60 * 1000

interface StoredRef {
  username: string
  captured_at: string // ISO 8601
}

function safeRead(): StoredRef | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(LOCALSTORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredRef>
    if (typeof parsed?.username !== 'string' || typeof parsed?.captured_at !== 'string') {
      return null
    }
    return { username: parsed.username, captured_at: parsed.captured_at }
  } catch {
    return null
  }
}

function safeWrite(value: StoredRef): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(value))
  } catch {
    // localStorage may throw in private mode / quota exceeded. The
    // capture is best-effort — silently drop.
  }
}

function safeRemove(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(LOCALSTORAGE_KEY)
  } catch {
    // ignore
  }
}

function isExpired(captured_at: string): boolean {
  const t = Date.parse(captured_at)
  if (!Number.isFinite(t)) return true
  return Date.now() - t > MAX_AGE_MS
}

// Reads ?ref= from the current URL and stores it. No-op when:
//   - not in a browser
//   - ?ref= is absent or empty after trim
//   - the existing stored ref is the same username (avoids
//     overwriting captured_at with a fresher timestamp on every
//     page load while the user navigates around)
//
// Idempotent and cheap. Safe to call on every app mount.
export function captureRefFromUrl(): void {
  if (typeof window === 'undefined') return
  let raw: string | null
  try {
    const params = new URLSearchParams(window.location.search)
    raw = params.get('ref')
  } catch {
    return
  }
  if (!raw) return
  const username = raw.trim().toLowerCase()
  if (!username) return

  const existing = safeRead()
  if (existing && !isExpired(existing.captured_at) && existing.username === username) {
    // Same ref already captured and still fresh — don't bump
    // captured_at. The 30-day window starts at the FIRST landing.
    return
  }

  safeWrite({
    username,
    captured_at: new Date().toISOString(),
  })
}

// Returns the parsed stored ref or null. Auto-clears entries older
// than MAX_AGE_DAYS so callers never see stale data.
export function getCapturedRef(): StoredRef | null {
  const ref = safeRead()
  if (!ref) return null
  if (isExpired(ref.captured_at)) {
    safeRemove()
    return null
  }
  return ref
}

// Removes the captured ref. Called on RPC success (so future
// applyAttributionIfPending calls in the same session no-op).
export function clearCapturedRef(): void {
  safeRemove()
}

interface ApplyAttributionResult {
  attributed: boolean
  affiliateId?: string
  error?: string
}

// Calls the SECURITY DEFINER RPC attribute_sub_account_to_affiliate.
// Best-effort — never throws. On terminal failure modes (e.g.
// already_attributed, affiliate_not_found_or_inactive,
// self_attribution_blocked), logs at console.warn and returns the
// error code without clearing localStorage. The caller decides what
// to do with non-success outcomes; the spec is silent no-op.
//
// On ok:true, clears localStorage so subsequent calls on the same
// page session no-op.
export async function applyAttributionIfPending(
  // deno-lint-ignore no-explicit-any
  supabase: SupabaseClient<any, any, any>,
  subAccountId: string,
): Promise<ApplyAttributionResult> {
  const ref = getCapturedRef()
  if (!ref) {
    return { attributed: false }
  }

  try {
    const { data, error } = await supabase.rpc('attribute_sub_account_to_affiliate', {
      p_sub_account_id: subAccountId,
      p_affiliate_username: ref.username,
    })

    if (error) {
      console.warn('[referralAttribution] RPC error', error)
      return { attributed: false, error: error.message }
    }

    const result = data as { ok: true; affiliate_id: string } | { ok: false; error: string } | null

    if (!result) {
      return { attributed: false, error: 'empty_response' }
    }

    if (result.ok === true) {
      clearCapturedRef()
      return { attributed: true, affiliateId: result.affiliate_id }
    }

    // Terminal failure modes from the RPC:
    //   not_authenticated, sub_account_id_required,
    //   affiliate_username_required, sub_account_not_found,
    //   not_owner, already_attributed,
    //   affiliate_not_found_or_inactive, self_attribution_blocked
    console.warn('[referralAttribution] attribution failed', result.error, {
      sub_account_id: subAccountId,
      ref_username: ref.username,
    })
    return { attributed: false, error: result.error }
  } catch (e) {
    console.warn('[referralAttribution] unexpected exception', e)
    return {
      attributed: false,
      error: e instanceof Error ? e.message : String(e),
    }
  }
}
